import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import Zone from "@insite/client-framework/Components/Zone";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button from "@insite/mobius/Button";
import Icon from "@insite/mobius/Icon";
import Typography from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import { Collapsable } from "@ucs/Components/Collapsable";
import { parserOptions } from "@ucs/Helpers/ApplicationSpecificationParser";
import ChevronRight from "@ucs/Icons/ChevronRight";
import UCSColors from "@ucs/Utils/UCSColors";
import parse from "html-react-parser";
import * as React from "react";
import { connect } from "react-redux";
import styled, { css } from "styled-components";

const ApplicationsSpecificationsContainer = styled("div")<{
    hasDetails: boolean;
    hasApplications: boolean;
    hasSpecifications: boolean;
}>`
    background-color: white;
    padding: ${p => (p.hasApplications || p.hasSpecifications ? "5% 5% 44px" : "0%")};
    height: 100%;
    h2 {
        margin-top: 0px;
    }
    width: ${p => {
        if (!p.hasDetails) {
            return "100%";
        }
        if (p.hasDetails) {
            return "50%";
        }
        if (!p.hasApplications && !p.hasSpecifications) {
            return "0%";
        }
    }};
    /* ul {
        list-style-type: disc;
        font-size: 18px;
        li {
            margin-bottom: 5px;
        }
    } */

    html,
    body {
        font-size: 18px;
    }
`;

const Selector = styled.div`
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    gap: 16px;
    margin-bottom: 25px;
`;

type Props = ReturnType<typeof mapStateToProps> & HasProductContext;

const mapStateToProps = (state: ApplicationState) => ({
    state,
});

const SelectorItem = styled("div")<{ selected: boolean; hasApplications: boolean; hasSpecifications: boolean }>`
    h2 {
        margin-bottom: 8px;
    }
    cursor: pointer;
    ${p => {
        if (p.hasApplications && p.hasSpecifications) {
            if (p.selected) {
                return css`
                    border-bottom: 2px solid ${getColor("primary")};
                `;
            }
            return css`
                color: ${UCSColors.gray04};
                border-bottom: 2px solid ${UCSColors.gray07};
            `;
        }
    }}
`;

const ContentContainer = styled.div`
    background: none;
    font-size: 18px;
    body {
        background: none;
    }
    ul {
        li {
            margin-bottom: 25px;
        }
    }
`;

const Container = styled.div`
    display: flex;
    flex-direction: row;
    flex-wrap: nowrap;
    h2 {
        margin-top: 0px;
    }
    background-color: white;
    ${({ theme }) =>
        breakpointMediaQueries(theme, [
            css`
                display: none;
            `,
            css`
                display: none;
            `,
        ])};
`;

const DetailsContainer = styled("div")<{ hasDetails: boolean; hasApplications: boolean; hasSpecifications: boolean }>`
    width: ${p => {
        if (p.hasDetails && !p.hasApplications && !p.hasSpecifications) {
            return "100%";
        }
        if (p.hasDetails) {
            return "50%";
        }
        return "0%";
    }};
    padding: ${p => (p.hasDetails ? "5%" : "0%")};
    background-color: white;
    body {
        background: none;
    }
`;

const MobileContainer = styled.div`
    display: none;
    ${({ theme }) =>
        breakpointMediaQueries(theme, [
            css`
                display: block;
            `,
            css`
                display: block;
            `,
        ])};
    ul {
        padding: 28px 0px;
        li {
            margin-bottom: 18px;
        }
    }
`;

const ApplicationSpecifications: React.FC<Props> = ({ productContext: { product, productInfo }, state }: Props) => {
    const [selectedView, selectView] = React.useState("application");
    const hasInit = React.useRef(false);
    const hasDetails = React.useRef(false);
    const details = React.useRef("");
    const hasApplications = React.useRef(false);
    const applications = React.useRef<string[]>([]);
    const hasSpecifications = React.useRef(false);
    const specifications = React.useRef("");

    if (!hasInit.current) {
        hasInit.current = true;
        if (product.specifications) {
            product.specifications.forEach(specification => {
                if (specification.name.toLowerCase() === "product specifications") {
                    hasSpecifications.current = true;
                    specifications.current = specification.htmlContent;
                } else if (specification.name.toLowerCase() === "product details") {
                    hasDetails.current = true;
                    details.current = specification.htmlContent;
                }
            });
        }

        if (product.attributeTypes) {
            product.attributeTypes.forEach(attributeType => {
                if (
                    attributeType.name.toLowerCase() === "productapplications" &&
                    attributeType.attributeValues &&
                    attributeType.attributeValues.length > 0
                ) {
                    hasApplications.current = true;
                    attributeType.attributeValues.forEach(attributeValue => {
                        applications.current.push(attributeValue.valueDisplay);
                    });
                }
            });
        }
    }

    if (!hasApplications.current && selectedView !== "specifications") {
        selectView("specifications");
    }

    return (
        <>
            <MobileContainer>
                {hasDetails.current && (
                    <Collapsable title="Details">{parse(details.current, parserOptions)}</Collapsable>
                )}
                {hasSpecifications.current && (
                    <Collapsable title="Specifications">{parse(specifications.current, parserOptions)}</Collapsable>
                )}
                {hasApplications.current && (
                    <Collapsable title="Applications">
                        <ul>
                            {applications.current.map((name, idx) => (
                                <li key={idx}>
                                    <Icon color="primary" size={16} src={ChevronRight} style={{ marginRight: 8 }} />
                                    {name}
                                </li>
                            ))}
                        </ul>
                    </Collapsable>
                )}
            </MobileContainer>
            <Container>
                {hasDetails.current && (
                    <DetailsContainer
                        hasDetails={hasDetails.current}
                        hasApplications={hasApplications.current}
                        hasSpecifications={hasSpecifications.current}
                    >
                        <Typography as="h2" variant="h2">
                            Details
                        </Typography>
                        <ContentContainer>{parse(details.current, parserOptions)}</ContentContainer>
                    </DetailsContainer>
                )}
                <ApplicationsSpecificationsContainer
                    hasDetails={hasDetails.current}
                    hasApplications={hasApplications.current}
                    hasSpecifications={hasSpecifications.current}
                >
                    <Selector>
                        {hasApplications.current && (
                            <SelectorItem
                                selected={selectedView === "application"}
                                onClick={() => selectView("application")}
                                hasApplications={hasApplications.current}
                                hasSpecifications={hasSpecifications.current}
                            >
                                <Typography as="h2" variant="h2">
                                    Applications
                                </Typography>
                            </SelectorItem>
                        )}
                        {hasSpecifications.current && (
                            <SelectorItem
                                selected={selectedView === "specifications"}
                                onClick={() => selectView("specifications")}
                                hasApplications={hasApplications.current}
                                hasSpecifications={hasSpecifications.current}
                            >
                                <Typography as="h2" variant="h2">
                                    Specifications
                                </Typography>
                            </SelectorItem>
                        )}
                    </Selector>
                    {hasApplications.current && (
                        <ContentContainer
                            style={selectedView === "application" ? { display: "block" } : { display: "none" }}
                        >
                            <ul>
                                {applications.current.map((name, idx) => (
                                    <li key={idx}>
                                        <Icon color="primary" size={16} src={ChevronRight} style={{ marginRight: 8 }} />
                                        {name}
                                    </li>
                                ))}
                            </ul>
                        </ContentContainer>
                    )}
                    {hasSpecifications.current && (
                        <ContentContainer
                            style={selectedView === "specifications" ? { display: "block" } : { display: "none" }}
                        >
                            {parse(specifications.current, parserOptions)}
                        </ContentContainer>
                    )}
                </ApplicationsSpecificationsContainer>
            </Container>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: withProductContext(connect(mapStateToProps)(ApplicationSpecifications)),
    definition: {
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
    },
};

export default widgetModule;
